import { computed, type UnwrapNestedRefs, type ComputedRef } from 'vue';
import { useHCommon } from './use-page-common';

export interface IAdminDataFetchOptions {
  url: string;
  filters: UnwrapNestedRefs<Record<string, any>>;
  pageSize?: number;
  additionalParams?: Record<string, any>;
}

export interface IAdminFormDataFetchOptions<T = any> {
  url: string;
  pageSize?: number;
  additionalParams?: Record<string, any>;
  formatter?: (records: any[]) => T[];
}

/**
 * Composable for fetching admin data with standard pagination and sorting
 *
 * @param options - Configuration options
 * @returns Object with data, loading, params, and execute function
 *
 * @example
 * const { data: bulletins, loading, params: bulletinsParams, execute: fetchBulletins } = useHAdminDataFetch({
 *   url: '/admin/bulletins',
 *   filters,
 *   pageSize: 25,
 *   additionalParams: { includebpmn: false }
 * });
 */
function resolvePositiveNumber(value: unknown, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function useHAdminDataFetch(options: IAdminDataFetchOptions) {
  const { url, filters, pageSize = 25, additionalParams = {} } = options;
  const { hHttpRequest } = useHCommon();

  // Keep page/pageSize on filters so table pagination and URL query stay in sync.
  // Bind also allowlists these keys if they are added after initialisation.
  if (filters.pageSize == null) {
    filters.pageSize = pageSize;
  }
  if (filters.page == null) {
    filters.page = 1;
  }

  const resolvePageSize = () => resolvePositiveNumber(filters.pageSize, pageSize);
  const resolvePage = () => resolvePositiveNumber(filters.page, 1);

  const { data, loading, params, execute } = hHttpRequest(
    {
      url,
      method: 'get',
      params: {
        query: (value: string) => value || '*',
        pagesize: () => resolvePageSize(),
        ...additionalParams,
        sort: () => {
          if (filters.sortBy && filters.sortOrder) {
            return `${filters.sortBy},${filters.sortOrder}`;
          } else {
            return undefined;
          }
        },
        startfrom: () => (resolvePage() - 1) * resolvePageSize(),
      },
      isLive: false,
      immediate: false,
    },
    { throwHttpErrors: false },
  );

  const totalResults = computed(() => (data.value as any)?.meta?.totalresults || 0);

  return {
    data,
    loading,
    params,
    execute,
    totalResults,
  };
}

/**
 * Composable for fetching admin form data (dropdowns, selects, etc.)
 *
 * @param options - Configuration options
 * @returns Object with formatted data and loading state
 *
 * @example
 * // Without formatter - returns raw records
 * const { data: workspaces, loading: workspacesLoading } = useHAdminFormDataFetch({
 *   url: '/admin/workspaces',
 * });
 *
 * @example
 * // With formatter - returns formatted data
 * const { data: workspaces, loading: workspacesLoading } = useHAdminFormDataFetch({
 *   url: '/admin/workspaces',
 *   formatter: (records) => records.map((workspace) => ({
 *     title: workspace.title,
 *     value: workspace.slug,
 *     key: workspace.slug,
 *   }))
 * });
 */
export function useHAdminFormDataFetch<T = any>(options: IAdminFormDataFetchOptions<T>) {
  const { url, pageSize = 200, additionalParams = {}, formatter } = options;
  const { hHttpRequest } = useHCommon();

  const { data: response, loading } = hHttpRequest(
    {
      url,
      method: 'get',
      params: {
        startfrom: 0,
        pagesize: pageSize,
        metaonly: false,
        includebpmn: false,
        fallback: false,
        query: '*',
        ...additionalParams,
      },
      isLive: true,
      immediate: true,
    },
    { throwHttpErrors: false },
  );

  const data: ComputedRef<T[]> = computed(() => {
    if (!(response.value as any)?.records) return [];

    if (formatter) {
      return formatter((response.value as any).records);
    }

    return (response.value as any).records;
  });

  return {
    data,
    loading,
    response,
  };
}

export interface IAdminDataSaveOptions {
  url: string;
  method?: 'post' | 'put' | 'patch' | 'delete';
  payload?: Record<string, any>;
  immediate?: boolean;
  headers?: Record<string, string>;
}

/**
 * Composable for saving/updating admin data. Must be called in setup context.
 *
 * @returns Object with save function
 *
 * @example
 * // In setup context
 * const { save } = useHAdminDataSave();
 *
 * // In a function
 * const result = await save({
 *   url: '/admin/agents',
 *   method: 'post',
 *   payload: { name: 'Agent Name', role: ['admin'] }
 * });
 */
export function useHAdminDataSave() {
  const { hHttpRequest } = useHCommon();

  const save = async (options: IAdminDataSaveOptions): Promise<{ ok: boolean; data?: any; error?: any }> => {
    const { url, method = 'post', payload = {}, immediate = false, headers = {} } = options;

    const result = await hHttpRequest(
      {
        url,
        method,
        payload,
        immediate,
      },
      {
        headers: {
          'x-onify-admin-pages': 'true',
          ...headers,
        },
      },
    ).execute();

    return result as { ok: boolean; data?: any; error?: any };
  };

  return {
    save,
  };
}
